import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

/* ─── Types ──────────────────────────────────────────────────────────── */

export interface EmployeeDropdownItem {
  _id?:  string;
  name?: string;
}

interface AddWalletArgs {
  employee:     string;
  walletAmount: number;
  isActive?:    boolean;
}

interface GetWalletsParams {
  status?: 'active' | 'inactive';
  search?: string;
}

/* ─── API ────────────────────────────────────────────────────────────── */

export const walletApi = createApi({
  reducerPath: 'walletApi',
  baseQuery:   dynamicBaseQuery,
  tagTypes:    ['Wallet'] as const,
  endpoints:   (builder) => ({
    getEmployeeDropdown: builder.query<{ data: EmployeeDropdownItem[] }, void>({
      query: () => ({ url: `/employees/dropdown`, method: 'GET' }),
      providesTags: ['Wallet'],
    }),
    addWallet: builder.mutation<unknown, AddWalletArgs>({
      query: (body) => ({ url: `/wallets`, method: 'POST', body }),
      invalidatesTags: ['Wallet'],
    }),
    getWallets: builder.query<unknown, GetWalletsParams | void>({
      query: (params) => {
        const { status, search: searchTerm } = params || {};
        const search = new URLSearchParams();
        if (status)     search.append('status', status);
        if (searchTerm) search.append('search', searchTerm);
        const qs = search.toString();
        return { url: qs ? `/wallets?${qs}` : `/wallets`, method: 'GET' };
      },
      providesTags: ['Wallet'],
    }),
    getMyWallet: builder.query<unknown, void>({
      query: () => ({ url: `/wallets/me`, method: 'GET' }),
      providesTags: ['Wallet'],
    }),
    getMyWalletTransactions: builder.query<unknown, void>({
      query: () => ({ url: `/wallets/me/transactions`, method: 'GET' }),
      providesTags: ['Wallet'],
    }),
    getWalletByEmployee: builder.query<unknown, string>({
      query: (employeeId) => ({ url: `/wallets/employee/${employeeId}`, method: 'GET' }),
      providesTags: ['Wallet'],
    }),
    getEmployeeWalletTransactions: builder.query<unknown, string>({
      query: (employeeId) => ({ url: `/wallets/employee/${employeeId}/transactions`, method: 'GET' }),
      providesTags: ['Wallet'],
    }),
  }),
});

export const {
  useGetEmployeeDropdownQuery,
  useAddWalletMutation,
  useGetWalletsQuery,
  useGetMyWalletQuery,
  useGetMyWalletTransactionsQuery,
  useGetWalletByEmployeeQuery,
  useGetEmployeeWalletTransactionsQuery,
} = walletApi;
